import { useEffect, useRef, useState } from 'react';
import { usePsychologistContext } from '../contexts/PsychologistContext';
import { useAuth } from '../contexts/AuthContext';
import psiPlatformAdapter from './psiPlatformAdapter';
import { getPsychologistMetrics } from './api/psychologist/metrics';

// Status reales de `psychologist_status` (mismo criterio que ProfileMetricsTab).
const DISCHARGE_STATUS_NAMES = ['pending discharged', 'discharged'];

export type JourneyMode = 'active' | 'pending-discharge' | 'graduated' | 'none';

// El ranking lo sirve psiplatform-be (/api/psychologist/metrics/ranking) con el id
// sacado del JWT. Antes se le pegaba al webhook de Make con la URL hardcodeada acá,
// que viajaba en el bundle de producción y aceptaba cualquier psychologist_id.

export function egresoAckKey(psychologistId: string) {
  return `psi-journey-egreso-ack-${psychologistId}`;
}

/**
 * Decide si la psicóloga ya puede acceder a Home o todavía está dentro del journey NZ
 * ("Tu camino inicial"). Solo puede entrar a Home si nunca fue NZ, o si se graduó y ya
 * confirmó el popup de egreso en Camino Inicial (mismo `egresoAckKey` que usa
 * ProfileMetricsTab/MetricsCaminoInicialTab).
 *
 * El webhook de ranking (`ranking_anterior`) solo se consulta cuando hace falta: si ya
 * sabemos por `rankingCategory` del perfil que sigue siendo NZ, o si ya confirmó el egreso,
 * no hay ambigüedad y nos ahorramos el fetch.
 *
 * `psychologist` del contexto solo lo populan hoy CalendarPage y PsychologistProfile — si esta
 * pantalla (Home, o el redirect de "/") es la primera en montar, nunca nadie lo carga y este hook
 * quedaría en `loading` para siempre (nunca se decide canAccessHome, nunca aparecen Pacientes).
 * Por eso lo pedimos nosotros mismos en cuanto sabemos el id, en vez de asumir que ya está.
 */
export function useNzJourneyStatus() {
  const { psychologist, loadPsychologist } = usePsychologistContext();
  const { psychologistId } = useAuth();
  const resolvedId = psychologistId || psychologist?.id || '';

  const [rankingAnterior, setRankingAnterior] = useState<string | null>(null);
  const [webhookLoading, setWebhookLoading] = useState(true);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const attemptedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (psychologist || !psychologistId) return;
    if (attemptedIdRef.current === psychologistId) return;
    attemptedIdRef.current = psychologistId;
    setProfileLoadFailed(false);
    loadPsychologist(psychologistId).catch(() => setProfileLoadFailed(true));
  }, [psychologist, psychologistId, loadPsychologist]);

  const psychologistReady = !!psychologist || profileLoadFailed;
  const isCurrentlyNZ = psychologist?.rankingCategory === 'NZ';
  const egresoAck = resolvedId ? localStorage.getItem(egresoAckKey(resolvedId)) === 'true' : false;

  useEffect(() => {
    let cancelled = false;

    if (!psychologistReady || !resolvedId || isCurrentlyNZ || egresoAck) {
      setWebhookLoading(false);
      return () => { cancelled = true; };
    }

    setWebhookLoading(true);
    psiPlatformAdapter
      .metricsRanking()
      .then((data) => {
        if (!cancelled) setRankingAnterior(data.ranking_anterior);
      })
      .catch(() => { /* ante error, se asume que no viene de NZ para no bloquear Home de más */ })
      .finally(() => { if (!cancelled) setWebhookLoading(false); });

    return () => { cancelled = true; };
  }, [resolvedId, psychologistReady, isCurrentlyNZ, egresoAck]);

  const loading = !psychologistReady || webhookLoading;
  const isPendingGraduationConfirm = !isCurrentlyNZ && !egresoAck && rankingAnterior === 'NZ';
  const canAccessHome = !loading && !isCurrentlyNZ && !isPendingGraduationConfirm;

  // `journeyMode` replica exactamente la lógica que tenía ProfileMetricsTab para decidir si
  // mostrar "Tu camino inicial" — ahora centralizada acá porque además de Perfil→Métricas la
  // necesita AppShell (para el tab de nivel superior "Camino Inicial").
  const isDischargeStatusReal = DISCHARGE_STATUS_NAMES.includes((psychologist?.statusName ?? '').toLowerCase());
  const wasEverNZ = isCurrentlyNZ || rankingAnterior === 'NZ';
  const isJustGraduatedReal = rankingAnterior === 'NZ' && !isCurrentlyNZ && !isDischargeStatusReal && !egresoAck;

  const [metrics, setMetrics] = useState<{ active_processes: number; inactive_processes: number } | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const needsMetrics = isDischargeStatusReal && wasEverNZ;

  useEffect(() => {
    let cancelled = false;
    if (!needsMetrics) { setMetricsLoading(false); return () => { cancelled = true; }; }
    setMetricsLoading(true);
    getPsychologistMetrics()
      .then((res) => { if (!cancelled) setMetrics(res); })
      .catch(() => { /* fail-closed: sin métricas, everHadProcesses da false más abajo */ })
      .finally(() => { if (!cancelled) setMetricsLoading(false); });
    return () => { cancelled = true; };
  }, [needsMetrics]);

  const everHadProcesses = ((metrics?.active_processes ?? 0) + (metrics?.inactive_processes ?? 0)) > 0;
  const journeyModeLoading = loading || (needsMetrics && metricsLoading);

  const journeyMode: JourneyMode = isDischargeStatusReal
    ? (wasEverNZ && everHadProcesses ? 'pending-discharge' : 'none')
    : isCurrentlyNZ
      ? 'active'
      : isJustGraduatedReal
        ? 'graduated'
        : 'none';

  return {
    loading, canAccessHome, egresoAck, isCurrentlyNZ,
    journeyMode, journeyModeLoading,
    isDischargeStatus: journeyMode === 'pending-discharge',
    isGraduated: journeyMode === 'graduated',
  };
}
